import type { EditorAction } from "../../editor/reducers";
import type { Dispatch } from "react";
import type { FigureRegionCreationTabFragment } from "../../client/generated";
import { ScrollArea } from "@acdc2/ui/components/scroll-area";
import { NavbarTabCloseButton } from "../../shell/NavbarTabCloseButton";
import { NavbarTabContent } from "../../shell/NavbarTabContent";
import { useTranslation } from "react-i18next";
import { makeTabIdentifier } from "../../editor/reducers";
import { assertTypeName } from "../../client/guards";
import { RegionUpdateForm } from "../RegionUpdateForm";
import {
  useCreateFigureRegionMutation,
  useFigureRegionCreationTabSuspenseQuery,
} from "../../client/generated";

type Props = {
  fragment: FigureRegionCreationTabFragment;
  dispatch: Dispatch<EditorAction>;
};

export function FigureRegionCreationTab({ fragment, dispatch }: Props) {
  const { t } = useTranslation();

  return (
    <NavbarTabContent
      value={makeTabIdentifier("figure", fragment.id)}
      title={t("FigureRegionCreationTab.title")}
      right={<NavbarTabCloseButton dispatch={dispatch} />}
    >
      <ScrollArea className="flex-1">
        <Body fragment={fragment} dispatch={dispatch} />
      </ScrollArea>
    </NavbarTabContent>
  );
}

function Body({ fragment, dispatch }: Props) {
  const figureAttributeIds = fragment.attributes
    .filter((attr) => attr.__typename === "FigureAttribute")
    .map((attr) => attr.id);

  const { data } = useFigureRegionCreationTabSuspenseQuery({
    variables: {
      regionId: fragment.id,
      figureAttributeIds,
    },
  });

  assertTypeName(data.region?.__typename === "Region");

  const [createRegion] = useCreateFigureRegionMutation();

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const result = await createRegion({
      variables: {
        regionId: fragment.id,
        excessAttributeIds: fragment.attributes.map((attr) => attr.id),
      },
      update: (cache) => {
        for (const attr of fragment.attributes) {
          const id = cache.identify(attr);
          cache.evict({ id });
          cache.gc();
        }
      },
    });

    return (
      !result.errors &&
      !result.data?.createFigureAttribute.userErrors?.length &&
      !result.data?.deleteAttributes.userErrors?.length
    );
  };

  return <RegionUpdateForm onSubmit={onSubmit} dispatch={dispatch} />;
}
